import * as React from "react";
import { Box, Typography, Divider, Chip } from "@mui/material";

// Maps deleted_counts keys (POST /admin/customers/:id/delete-data response)
// to their display labels. Order here is the display order.
const COUNT_LABELS = [
  ["customer_access_deleted", "Access grants removed"],
  ["patient_form_submissions_deleted", "Patient form submissions"],
  ["test_analysis_deleted", "Lab analysis / results"],
  ["order_qrcodes_deleted", "QR code labels"],
  ["shipping_labels_deleted", "DHL shipping labels"],
  ["order_progress_deleted", "Progress history entries"],
  ["order_progress_reset_to_unprocessed", "Tests reset to Unprocessed"],
  ["reports_deleted", "Report PDFs"],
  ["therapist_documents_deleted", "Therapist documents"],
];

function Row({ label, value, warn }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
      <Typography sx={{ fontSize: "0.8125rem", color: "#374151" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: warn ? "#b91c1c" : "#111827" }}>
        {value}
      </Typography>
    </Box>
  );
}

// Renders the `data` object from a successful delete-data response. Used both
// inline (once a customer's data has been deleted) and inside the confirm
// dialog's result step.
function DeletionSummary({ result }) {
  if (!result) return null;

  const counts = result.deleted_counts || {};
  const shadowUser = result.shadow_user || {};
  const ordersRetained = result.orders_retained || [];
  const reportsFailed = counts.reports_failed || [];
  const documentsFailed = counts.therapist_documents_failed || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
          Deleted
        </Typography>
        {COUNT_LABELS.map(([key, label]) => (
          <Row key={key} label={label} value={counts[key] ?? 0} />
        ))}
        {(reportsFailed.length > 0 || documentsFailed.length > 0) && (
          <Row
            label="Failed to delete (S3)"
            value={reportsFailed.length + documentsFailed.length}
            warn
          />
        )}
      </Box>

      <Divider />

      <Box>
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
          Login state
        </Typography>
        <Row label="Portal login reset" value={shadowUser.reset ? "Yes" : "No"} />
        {shadowUser.skipped_reason && <Row label="Skipped reason" value={shadowUser.skipped_reason} warn />}
        <Row label="Therapist role removed" value={shadowUser.therapist_role_removed ? "Yes" : "No"} />
        <Row label="Therapist applications deleted" value={shadowUser.therapist_applications_deleted ?? 0} />
      </Box>

      <Divider />

      <Box>
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
          Orders kept (data untouched)
        </Typography>
        {ordersRetained.length === 0 ? (
          <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af" }}>None</Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {ordersRetained.map((o) => (
              <Chip key={o.id} size="small" label={`#${o.order_number}`} />
            ))}
          </Box>
        )}
      </Box>

      {result.deletion_request_id != null && (
        <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af" }}>
          Deletion request #{result.deletion_request_id}
        </Typography>
      )}
    </Box>
  );
}

export default DeletionSummary;
